import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import AuthPageShell from './AuthPageShell';
import {
  AnimatedButton,
  AnimatedSuccess,
  PasswordStrengthIndicator,
} from '../animations/AnimatedFormElements';
import '../../styles/common.css';
import '../../styles/animations.css';

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }

    if (name === 'password') {
      let strength = 0;
      if (value.length >= 8) strength += 1;
      if (/[A-Z]/.test(value)) strength += 1;
      if (/[a-z]/.test(value)) strength += 1;
      if (/[0-9]/.test(value)) strength += 1;
      if (/[^A-Za-z0-9]/.test(value)) strength += 1;
      setPasswordStrength(strength);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password =
        'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.confirmPassword !== formData.password) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!token) {
      setApiError('Invalid or missing reset link. Please request a new one.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/users/resetPassword`, {
        reset_password_token: token,
        password: formData.password,
      });

      if (response.data.code === 200) {
        setIsSubmitted(true);
      } else {
        setApiError(response.data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setApiError(
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          'An error occurred. Please try again or request a new reset link.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const title = isSubmitted ? 'Password Reset Complete' : 'Reset Password';
  const subtitle = isSubmitted
    ? 'Your password has been successfully reset'
    : 'Create a new secure password for your account';

  return (
    <AuthPageShell
      title={title}
      subtitle={subtitle}
      footer={
        <p>
          Need help?{' '}
          <button type="button" className="btn-link">
            Contact Support
          </button>
        </p>
      }
    >
      {!token && !isSubmitted ? (
        <div style={{ textAlign: 'center' }}>
          <p className="form-error mb-3">
            Invalid or missing reset link. Please use the link from your email or request a new
            one.
          </p>
          <AnimatedButton
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/auth/forgot-password')}
          >
            Request Reset Link
          </AnimatedButton>
        </div>
      ) : !isSubmitted ? (
        <form onSubmit={handleSubmit}>
          {apiError ? (
            <div className="form-error error-shake mb-3" style={{ textAlign: 'center' }}>
              {apiError}
            </div>
          ) : null}

          <div className="form-group animated-form-group">
            <label htmlFor="password" className="form-label">
              New Password
            </label>
            <div className="auth-password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className="form-input form-input-animated"
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide new password' : 'Show new password'}
                tabIndex={-1}
              >
                {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {errors.password ? (
              <div className="form-error error-shake">{errors.password}</div>
            ) : null}

            {formData.password ? <PasswordStrengthIndicator strength={passwordStrength} /> : null}

            <div className="form-text">
              Password must be at least 8 characters and include uppercase, lowercase, and numbers
            </div>
          </div>

          <div className="form-group animated-form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <div className="auth-password-field">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                className="form-input form-input-animated"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                tabIndex={-1}
              >
                {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              </button>
            </div>
            {errors.confirmPassword ? (
              <div className="form-error error-shake">{errors.confirmPassword}</div>
            ) : null}
          </div>

          <AnimatedButton
            type="submit"
            className="btn btn-primary btn-block"
            disabled={isLoading}
            isLoading={isLoading}
          >
            Reset Password
          </AnimatedButton>
        </form>
      ) : (
        <AnimatedSuccess
          title="Password Reset Complete"
          message="Your password has been successfully reset. You can now use your new password to log in."
          actionButton={
            <AnimatedButton
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/auth/login')}
            >
              Back to Login
            </AnimatedButton>
          }
        />
      )}
    </AuthPageShell>
  );
};

export default ResetPassword;
